import { ClassesStore } from '@/classes/store/classes.store';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Prop } from 'vue-property-decorator';
import { CalendarEventDetail } from '@webcat/commons-web-components/dist/calendar/models/calendar-event-detail';
import { ScheduleDetails } from '@/tools/models/class-builder.models';
import { Holiday } from '@/tools/models/holiday.model';
import EdGenericCalendar from '@/core/components/ed-generic-calendar/ed-generic-calendar.component.vue'
import { EdContextMenuModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { BUILDER_DAY_OPTIONS } from '@/courses/values/courses.values';

@Component({
  name: 'ed-class-builder-placeholder-calendar',
  components: {
    EdClassBuilderPlaceholderBlock: () => import(/* EdClassBuilderPlaceholderBlock */ '@/tools/components/class-builder/ed-class-builder-placeholder-block/ed-class-builder-placeholder-block.component.vue'),
    EdGenericCalendar,
  }
})
export default class EdClassBuilderPlaceholderCalendarComponent extends Vue {
  @Prop({ default: null }) readonly scheduleDetails: ScheduleDetails;

  @Emit('zoom')
  emitZoom(weekday: number, start: string, end: string) {
    return {
      weekday,
      start,
      end
    }
  }

  schooldDays: string[] = [];
  colorDay: string = '#fff'
  holidays: any[] = [];
  calendarDays: Record<string, any> = {};
  eventSuffix: Record<string, any> = {};
  holidayEventColor = 'bisque';

  get months(): any[] {
    return this.getMonthsCalendar();
  }

  get events(): any[] {
    return this.getCalendarEvents(this.scheduleDetails, this.holidays);
  }

  get isAbleToEdit(){
    return this.$store.state.ClassesStore.classSchedule?.isAbleToEdit;
  }

  getMonthsCalendar(): any[] {
    let months = [];

    const [firstYear, firstMonth] = [
      Number(Object.keys(this.scheduleDetails)[0].split('-')[0]),
      Number(Object.keys(this.scheduleDetails)[0].split('-')[1]),
    ]
    
    const [lastYear, lastMonth] = [
      Number(Object.keys(this.scheduleDetails)[Object.keys(this.scheduleDetails).length- 1].split('-')[0]),
      Number(Object.keys(this.scheduleDetails)[Object.keys(this.scheduleDetails).length- 1].split('-')[1])
    ]

    for (
      let i = firstMonth;
      i <= (firstYear === lastYear ? lastMonth : 12);
      i++
    ) {
      let month;
      month = new Date(firstYear, i - 1);
      
      months.push({
        start: month.toISOString().split('T')[0],
        end: new Date(firstYear, i - 1, 0).toISOString().split('T')[0],
        name: month.toLocaleString('default', { month: 'long' }),
      });
    }

    if(lastYear !== firstYear) {
      for(let i = 1; i <= lastMonth; i++) {
        let month;
        month = new Date(lastYear, i - 1);
        
        months.push({
          start: month.toISOString().split('T')[0],
          end: new Date(lastYear, i - 1, 0).toISOString().split('T')[0],
          name: month.toLocaleString('default', { month: 'long' }),
        });
      }
    }

    return months;
  }

  getCalendarEvents(scheduleDetails: ScheduleDetails, holidays: Holiday[]): CalendarEventDetail[] {
    const events = [];
    let localHolidays = holidays.filter((holiday: Holiday) => {  //Exclueded secial day marked as IGNORED
      if (holiday.official === true) return true;
      if (holiday.official === false && holiday.status !== BUILDER_DAY_OPTIONS.IGNORED) return true;
      return false;
    });
    
    Object.keys(scheduleDetails).map((key) => {
      const dateDetails = scheduleDetails[key];

      dateDetails.forEach((data: any) => {
        if (data) {
          const hasMoreThanOnePlaceholder = data.otherPlaceholder.length > 0;
          const baseData = {
            startDate: this._formatDate(`${key} ${data.startTime}`),
            endDate: this._formatDate(`${key} ${data.endTime}`),
            allDay: true,
          };

          events.push({
            ...baseData,
            uid: `${data.placeholder.id}_${key}_${data.startTime}`,
            color: data.placeholder?.color ?? this.holidayEventColor,
            summary: data.placeholder?.title ?? '',
            hasMoreThanOnePlaceholder,
          });

          if (data.otherPlaceholder.length > 0) {
            data.otherPlaceholder.forEach((otherPlaceholder: any) => {
              events.push({
                ...baseData,
                uid: `${otherPlaceholder.id}_${key}_${data.startTime}`,
                color: otherPlaceholder?.color ?? this.holidayEventColor,
                summary: otherPlaceholder?.title ?? '',
                hasMoreThanOnePlaceholder,
              });
            });
          } 
        }
      });
    });

    localHolidays.forEach((holiday: Holiday) => {
      if(holiday.endDate) {
        const interval = getDays(new Date(holiday.startDate), new Date(holiday.endDate));

        for(let i = 0; i <= interval; i++) {
          const date = new Date(holiday.startDate);
          date.setDate(date.getDate() + i);
          const day = date.toISOString().split('T')[0];
          events.push({
            uid: `${holiday.holidayPlanId}_${holiday.startDate}`,
            startDate: this._formatDate(`${day}`),
            endDate: this._formatDate(`${day}`),
            allDay: true,
            hasMoreThanOnePlaceholder: false,
            color: this.holidayEventColor,
            summary: holiday?.name ?? '',
          })
        }

      } else {
        events.push({
          uid: `${holiday.holidayPlanId}_${holiday.startDate}`,
          startDate: this._formatDate(`${holiday.startDate} 00:00`),
          endDate: this._formatDate(`${holiday.startDate} 00:00`),
          allDay: true,
          hasMoreThanOnePlaceholder: false,
          color: this.holidayEventColor,
          summary: holiday?.name ?? '',
        })
      }
    })

    return events;
  }

  private _formatDate(dateString: string) : Date {
    if (!dateString) {
      return null;
    }
    const dateSplitBySpace =  dateString.split(' ');
    if (dateSplitBySpace.length > 1) {
      return new Date(dateString);
    } else {
      const arrayDate = dateString?.split('-');
      return new Date(parseInt(arrayDate[0]), parseInt(arrayDate[1]) - 1, parseInt(arrayDate[2]));
    }
  }

  private _formatTime(date: Date) : string {
    if (!date) {
      return null;
    }
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  created() {
    this.schooldDays = this.$route.meta.schoolDays;
    this.colorDay = this.$route.meta.colorDays;
    this.holidays = this.$route.meta.holidays;
    this._buildCalendarDays();
    this._buildEventSuffix();
  }

  mounted() {
    this.$el.querySelectorAll('[data-is-school-day="true"]').forEach((el: HTMLDivElement) => {
      const parent = el.parentElement.parentElement
      parent.style.backgroundColor = addAlpha(this.colorDay, 0.4);
    });
    this.$el.querySelectorAll('[data-is-holiday="true"]').forEach((el: HTMLDivElement) => {
      const parent = el.parentElement.parentElement
      parent.style.backgroundColor = addAlpha('#ED5454', 0.4);
    });
  }

  private _buildCalendarDays() {
    for (let i = 0; i < this.schooldDays.length; i++) {
      const day = this.schooldDays[i];
      this.calendarDays[day] = { color: addAlpha(this.colorDay, 0.4) };
    }
  }

  private _buildEventSuffix() {
    this.eventSuffix = {
      expressions: [
          {
            target: '.search', attribute: 'style', booleanAttribute: false,
            expression: this._eventSuffixExpression,
            applied: 'display: inline;', fallback: 'display: none;'
          },
          {
            target: '.caretDown', attribute: 'style', booleanAttribute: false,
            expression: this._eventSuffixExpression,
            applied: 'display: inline;', fallback: 'display: none;'
          }
        ],
      events: [
        { 
          target: '.search', 
          handler: this._eventOnClick
        },
      ]
    };
  }

  private _eventSuffixExpression(event): boolean {
    const foundEvent = this.events.find(e => e.uid === event.uid);
    return foundEvent?.hasMoreThanOnePlaceholder ?? false;
  }

  private _eventOnClick(event) {
    const uid = event.detail.uid;
    if (uid) {
      const foundEvent = this.events.find(e => e.uid === uid);
      if (foundEvent) {
        this.emitZoom(event.weekday, this._formatTime(foundEvent.startDate), this._formatTime(foundEvent.endDate));
      }
    }
  }

  public onWcCalendarEventMouseOver(customEvent: CustomEvent): void {
    const mouseEvent: MouseEvent = customEvent?.detail?.value?.mouseEvent;
    const eventData: any = customEvent?.detail?.value?.eventData;
    if (mouseEvent && eventData) {
      this.$EdContextMenu.show(new EdContextMenuModel({
        position: { x: mouseEvent.x, y: mouseEvent.y },
        options: [{ text: eventData.summary }],
        darkMode: true
      }));
    }
  }

  public onWcCalendarEventMouseLeave(): void {
    this.$EdContextMenu.close();
  }
}

function addAlpha(color: string, opacity: number) {
  // coerce values so ti is between 0 and 1.
  var _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
}

const getDays = (date_1: Date, date_2: Date): number =>{
    let difference = date_1.getTime() - date_2.getTime();
    let TotalDays = Math.ceil(difference / (1000 * 3600 * 24));
    return Math.abs(TotalDays);
}